/**
 * 画面に出る日本語はすべてここに集約する。
 * 実装が条件を満たしていない文言を表示してはならない（特にプライバシー関連）。
 */
export const ja = {
  meta: {
    title: '事件番号表記変換ツール｜表記ゆれを統一・無料',
    description:
      '「R5」「令5」などの元号略称、漢数字・アラビア数字、全角・半角といった事件番号の表記ゆれを、「令和5年（受）第2207号」の形式に一括統一します。事件番号を含む文章をそのまま貼り付けられ、1件ずつ、または一括でコピーできます。',
  },

  hero: {
    h1: '事件番号の表記ゆれを統一',
    lead: '「R5」「令5」などの元号略称、漢数字・アラビア数字、全角・半角などの表記ゆれを一括統一します。',
    sub: '事件番号を含む文章を、そのまま貼り付けるだけです。',
  },

  input: {
    label: '入力内容',
    placeholder:
      'ここに文章を貼りつけてください。例：本件では、R5（受）2207及び令和4年（行ヒ）第283号が参照されています。',
    helper: '事件番号を含む文章でも、事件番号だけでも入力できます。',
    /**
     * 入力欄の直下に出す注意書き。
     * 「入れないでほしいもの」だけに絞り、取扱いの詳細は
     * `security` の折りたたみへ逃がす（日本の申込フォームの慣行）。
     */
    handlingNotice: '個人情報や機密情報など、変換に不要な情報は入力しないでください。',
    handlingLink: '入力内容の取扱いについて',
    maxLabel: '最大20,000文字',
    sample: '例文を入力',
    sampleAriaLabel: '例文を入力欄に読み込む',
    counter: (n: number, max: number) =>
      `${n.toLocaleString('ja-JP')} / ${max.toLocaleString('ja-JP')}文字`,
  },

  actions: {
    submit: '事件番号を変換',
    copy: 'コピー',
    copied: 'コピーしました',
    copyAll: 'すべてコピー',
    clear: 'クリア',
  },

  /** 入力内容の取扱い。折りたたみの中に置く。 */
  security: {
    heading: '入力内容の取扱いについて',
    // 変換処理がブラウザ内だけで完結していることが前提の文言。
    // 入力内容を外部へ送る処理を追加する場合は、必ずここを先に直すこと（README §3）。
    // 「通信は一切発生しません」とは書かない。ページ読み込みとサイトのアクセス解析は発生する。
    points: [
      '入力内容はブラウザ内で処理され、サーバーに送信されません。',
      '入力内容は保存されません。ページを再読み込みすると残りません。',
    ],
  },

  results: {
    heading: '変換結果',
    count: (n: number) => `${n}件の事件番号を抽出しました`,
    emptyTitle: '事件番号が見つかりませんでした。',
    emptyBody: '入力内容に事件番号が含まれているか、表記をご確認ください。',
    contextInferred: '文脈から補完',
    pendingHeading: (n: number) => `確認が必要な事件番号があります（${n}件）`,
    pendingNote:
      '元号を選択すると、変換結果に追加されます。選択前の候補は「すべてコピー」には含まれません。',
    pendingEraTitle: '元号を確認してください',
    pendingChoiceAriaLabel: (label: string, normalized: string) =>
      `元号を${label}として確定し、${normalized} に変換する`,
    pendingInfoTitle: '入力内容をご確認ください',
    warningUnknownSymbol: '一般的な事件符号の一覧に無い符号です。表記はそのまま出力しています。',
    warningEraOutOfRange: 'その元号に存在しない年の可能性があります。',
    copyAllAriaLabel: '変換結果をすべてコピー',
    copyItemAriaLabel: (n: string) => `${n} をコピー`,
  },

  /**
   * 変換はブラウザ内で同期的に完結するため、通信・タイムアウト・
   * レートリミットのエラーは存在しない。文字数超過だけを扱う。
   */
  errors: {
    tooLong: '入力できる文字数は20,000文字までです。',
  },

  disclaimer:
    '本ツールは事件番号の表記を整えるものであり、事件の存在、裁判所、事件内容の正確性を確認するものではありません。',

  about: {
    heading: '事件番号とは',
    body: '事件番号は、裁判所が受け付けた事件を特定するための番号です。「元号・年」「事件符号」「通し番号」の3つの要素で構成され、たとえば「令和5年（受）第2207号」のように表記します。事件符号は事件の種類ごとに定められており、民事上告受理申立事件であれば「受」、行政上告受理申立事件であれば「行ヒ」などが使われます。',
  },

  formats: {
    heading: '本ツールが対応する表記例',
    rows: [
      { from: 'R5(受)2207', to: '令和5年（受）第2207号' },
      { from: '令5（受）2207', to: '令和5年（受）第2207号' },
      { from: '令和五年（受）第二二〇七号', to: '令和5年（受）第2207号' },
      { from: '令和 ５ 年 （ 受 ） 第 ２２０７ 号', to: '令和5年（受）第2207号' },
      { from: 'R1（ネ）0003', to: '令和元年（ネ）第3号' },
      { from: '2023（受）2207', to: '令和5年（受）第2207号' },
      {
        from: '平成26年（オ）第77号及び同年（受）第93号',
        to: '平成26年（オ）第77号 / 平成26年（受）第93号',
      },
    ],
  },

  faq: {
    heading: 'よくあるご質問',
    items: [
      {
        q: '入力した文章は保存されますか？',
        a: '保存されません。入力内容はブラウザ内で処理され、サーバーに送信されないため、データベースにもログにも残りません。ページを再読み込みすると入力内容は残りません。',
      },
      {
        q: '西暦で書かれた事件番号も変換できますか？',
        a: '変換できます。ただし1912年、1926年、1989年、2019年は1つの西暦年に2つの元号が含まれるため、年だけでは元号を確定できません。この場合は自動で変換せず、「確認が必要な事件番号」として2つの元号を提示します。該当する元号を選択すると、そのまま変換結果に追加されます。',
      },
      {
        q: '「同年」のように省略された書き方にも対応しますか？',
        a: '対応します。同一文または同一段落内の直前の記載から一意に判断できる場合にかぎり、省略された元号・年を補完します。直前に複数の年が記載されていて特定できない場合は、推測せず「確認が必要な事件番号」として表示します。',
      },
      {
        q: '事件が実際に存在するかどうかも確認できますか？',
        a: '確認できません。本ツールは事件番号の表記を統一するためのものであり、事件の存在、裁判所、事件名、事件内容の正確性は判定しません。',
      },
      {
        q: '法令の条文番号が事件番号として抽出されることはありますか？',
        a: 'ありません。「民法709条」「会社法第309条第2項」のような条文番号、法令番号、電話番号、郵便番号、金額、日付、文書管理番号は事件番号として抽出しない設計です。',
      },
    ],
  },

  footer: {
    note: '本ツールは Lawsnote が無料で提供しています。',
    blog: 'Lawsnote JP Blog',
    blogUrl: 'https://lawsnote.com/jp/',
    policy: '利用規約＆プライバシーポリシー',
    policyUrl: 'https://lawsnote.com/jp/privacy-policy/',
    copyright: 'Copyright © 2026 Lawsnote Japan. All Rights Reserved.',
  },
} as const;
